import { createFileRoute } from "@tanstack/react-router";
import { Helmet } from "react-helmet-async";
import { MarketingLayout } from "@/components/marketing/MarketingLayout";
import { Section } from "@/components/marketing/layout/Section";
import { enquiryMailto } from "@/lib/enquiry";
import { ENQUIRY_EMAIL } from "@/lib/brand";

export const Route = createFileRoute("/legal/refund-policy")({
  component: RefundPolicyPage,
});

function RefundPolicyPage() {
  return (
    <MarketingLayout>
      <Helmet>
        <title>Refund Policy | Larasoft</title>
        <meta
          name="description"
          content="Learn about Larasoft's refund policy for our software-as-a-service products and services."
        />
        <link
          rel="canonical"
          href="https://larasoft.global/legal/refund-policy"
        />
      </Helmet>
      <Section className="py-16">
        <div className="mx-auto max-w-4xl">
          <h1 className="mb-8 text-4xl font-bold">Refund Policy</h1>

          <div className="prose prose-slate max-w-none">
            <p className="lead text-lg text-slate-700">
              Last updated: {new Date().toLocaleDateString("en-ZA")}
            </p>

            <section className="mb-12">
              <h2 className="mb-4 text-2xl font-semibold">Overview</h2>
              <p>
                This Refund Policy describes how Larasoft (Pty) Ltd
                (&ldquo;Larasoft&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;, or
                &ldquo;our&rdquo;) handles refund requests for our
                software-as-a-service (SaaS) products and services.
              </p>
              <p>
                We are committed to providing high-quality software solutions. If
                you are not satisfied with our services, please review the terms
                below to understand your options.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="mb-4 text-2xl font-semibold">
                30-Day Refund Window
              </h2>
              <p>
                We offer a <strong>full refund</strong> within{" "}
                <strong>30 days</strong> of your initial subscription purchase,
                subject to the following conditions:
              </p>
              <ul className="mt-4 space-y-2">
                <li>
                  The refund request must be submitted within 30 days of your
                  first payment
                </li>
                <li>
                  You must not have violated our Terms of Service or Acceptable
                  Use Policy
                </li>
                <li>
                  The refund applies to your initial subscription payment only
                </li>
              </ul>
              <p className="mt-4">
                To request a refund during this period, contact us at{" "}
                <a
                  href={enquiryMailto("/legal/refund-policy")}
                  className="text-blue-600 underline"
                >
                  {ENQUIRY_EMAIL}
                </a>{" "}
                with your account details and reason for the request. We aim to
                process all refund requests within 5 business days.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="mb-4 text-2xl font-semibold">After 30 Days</h2>
              <p>
                <strong>
                  No refunds will be issued after the 30-day refund window has
                  expired.
                </strong>
              </p>
              <p className="mt-4">This includes:</p>
              <ul className="mt-4 space-y-2">
                <li>
                  Subsequent monthly or annual subscription renewals
                </li>
                <li>Upgrades or plan changes</li>
                <li>Add-on services or features</li>
                <li>Partial months of service</li>
              </ul>
              <p className="mt-4">
                You may cancel your subscription at any time, and you will
                retain access to the service until the end of your current
                billing period. No pro-rated refunds will be provided for unused
                portions of a billing cycle.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="mb-4 text-2xl font-semibold">
                Subscription Cancellation
              </h2>
              <p>
                You may cancel your subscription at any time through your
                account dashboard or by contacting our support team.
              </p>
              <p className="mt-4">Upon cancellation:</p>
              <ul className="mt-4 space-y-2">
                <li>
                  Your subscription will remain active until the end of the
                  current billing period
                </li>
                <li>No further charges will be processed</li>
                <li>
                  Access to the service will terminate at the end of the paid
                  period
                </li>
                <li>
                  You are responsible for exporting any data before your access
                  ends
                </li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="mb-4 text-2xl font-semibold">
                Non-Refundable Items
              </h2>
              <p>
                The following are{" "}
                <strong>not eligible for refunds</strong> under any
                circumstances:
              </p>
              <ul className="mt-4 space-y-2">
                <li>
                  Payment processing fees (charged by our payment provider)
                </li>
                <li>Custom development or professional services</li>
                <li>
                  Training or consultation services already delivered
                </li>
                <li>One-time setup fees, where applicable</li>
                <li>
                  Services terminated due to Terms of Service violations
                </li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="mb-4 text-2xl font-semibold">
                Exceptional Circumstances
              </h2>
              <p>
                We may, at our sole discretion, consider refunds outside the
                standard 30-day window in the following circumstances:
              </p>
              <ul className="mt-4 space-y-2">
                <li>
                  Extended service outages caused by Larasoft (exceeding our
                  published SLA commitments)
                </li>
                <li>Billing errors on our part</li>
                <li>Duplicate charges</li>
              </ul>
              <p className="mt-4">
                Requests for exceptional refunds must be submitted in writing
                to{" "}
                <a
                  href={enquiryMailto("/legal/refund-policy")}
                  className="text-blue-600 underline"
                >
                  {ENQUIRY_EMAIL}
                </a>{" "}
                with supporting documentation.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="mb-4 text-2xl font-semibold">
                Refund Processing
              </h2>
              <p>Approved refunds are processed as follows:</p>
              <ul className="mt-4 space-y-2">
                <li>Refunds are issued to the original payment method</li>
                <li>
                  Processing typically takes{" "}
                  <strong>3-10 business days</strong>, depending on your
                  financial institution
                </li>
                <li>
                  Payment processing fees originally charged by our payment
                  provider are non-refundable and will be deducted from any
                  refund amount
                </li>
                <li>
                  You will receive email confirmation once the refund has been
                  initiated
                </li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="mb-4 text-2xl font-semibold">Free Trials</h2>
              <p>If we offer a free trial period:</p>
              <ul className="mt-4 space-y-2">
                <li>No payment is required during the trial</li>
                <li>
                  You will not be charged if you cancel before the trial ends
                </li>
                <li>
                  If you do not cancel, your subscription will begin
                  automatically, and the applicable fees will be charged
                </li>
                <li>
                  The 30-day refund window begins from your first paid
                  subscription date, not the trial start date
                </li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="mb-4 text-2xl font-semibold">
                How to Request a Refund
              </h2>
              <p>To submit a refund request:</p>
              <ol className="mt-4 list-decimal space-y-2 pl-6">
                <li>
                  Email us at{" "}
                  <a
                    href={enquiryMailto("/legal/refund-policy")}
                    className="text-blue-600 underline"
                  >
                    {ENQUIRY_EMAIL}
                  </a>
                </li>
                <li>
                  Include your registered email address and account name
                </li>
                <li>
                  Provide the date of purchase and transaction reference
                </li>
                <li>Briefly explain the reason for your request</li>
              </ol>
              <p className="mt-4">
                We will acknowledge your request within 2 business days and
                provide a decision within 5 business days.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="mb-4 text-2xl font-semibold">
                Changes to This Policy
              </h2>
              <p>
                We reserve the right to modify this Refund Policy at any time.
                Changes will be effective immediately upon posting to our
                website. Your continued use of our services after any changes
                constitutes acceptance of the updated policy.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="mb-4 text-2xl font-semibold">Contact Us</h2>
              <p>
                If you have questions about this Refund Policy, please contact
                us:
              </p>
              <div className="mt-4 rounded-lg bg-slate-50 p-4">
                <p className="mb-2">
                  <strong>Email:</strong>{" "}
                  <a
                    href={enquiryMailto("/legal/refund-policy")}
                    className="text-blue-600 underline"
                  >
                    {ENQUIRY_EMAIL}
                  </a>
                </p>
                <p>
                  <strong>Website:</strong>{" "}
                  <a
                    href="https://larasoft.global"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 underline"
                  >
                    larasoft.global
                  </a>
                </p>
              </div>
              <p className="mt-6 text-sm italic text-slate-600">
                This policy is governed by the laws of the Republic of South
                Africa.
              </p>
            </section>
          </div>
        </div>
      </Section>
    </MarketingLayout>
  );
}
